/**
 * The two rows above the table (H8, H9).
 *
 * The first is the app: the wordmark, the way into the Table menu, and how
 * long this turn has. The second is the game: what you are playing on the
 * left, where you stand on the right.
 *
 * Splitting them that way is what lets either one be missing. A watcher has no
 * turn and a game with no score has nothing for the right-hand panel, and in
 * both cases the row simply has less in it rather than collapsing.
 *
 * ## The fields it reads
 *
 * `targetScore` and `handNumber` are platform conventions, like `trickPlays`
 * and `reshuffles` before them - fixed names with fixed shapes, documented in
 * the toolkit's layout module. A game that publishes them gets "First to 500"
 * and "Hand 3"; one that does not simply says less. Neither is declared per
 * game, because neither is a choice: a target score is a target score.
 *
 * The score half is declared, though, because whose score it is genuinely
 * varies - `teams` for a partnership game, `seatScore` otherwise. Those are
 * layout declarations and always were.
 */

import type { MatchView } from '../net/match.js';
import type { GameLayoutWire } from '../net/session.js';
import { Icon } from '../render/Icon.js';
import { gameArt } from '../render/game-art.js';
import { sidesOf } from '../render/teams.js';

/** A number the view published under a platform name, or null. */
function numberField(view: MatchView, field: string): number | null {
  const raw = view.view[field];
  return typeof raw === 'number' ? raw : null;
}

export function TableTopBar({
  view,
  layout,
  gameName,
  status,
  onMenu,
}: {
  view: MatchView;
  layout: GameLayoutWire;
  gameName: string;
  /** Turn pill, clock, watcher count - whatever the turn needs saying about. */
  status: React.ReactNode;
  onMenu: () => void;
}) {
  /*
   * Keyed on the game's id alone.
   *
   * `gameArt` takes a family as a fallback for a game it has never seen, and
   * every game with a layout is one it has seen - the layout is how it got
   * here. Passing an empty family says that rather than inventing one.
   */
  const art = gameArt(layout.gameId, '');

  const target = numberField(view, 'targetScore');
  const hand = numberField(view, 'handNumber');

  const sides = sidesOf(view, layout);
  const myScore = (() => {
    const field = layout.seatScore?.field;
    if (!field || view.seat === null) return null;
    const raw = view.view[field];
    if (!Array.isArray(raw)) return null;
    const value = (raw as unknown[])[view.seat];
    return typeof value === 'number' ? value : null;
  })();

  const hasPanel = hand !== null || sides !== null || myScore !== null;

  return (
    <>
      <div className="table-bar">
        {/*
          What you are playing, and what winning means.

          "First to 500" is the one piece of a game's rules that changes how
          you play every hand of it, which is why it earns a place here rather
          than living in the menu with the rest.

          Top-left, in the row with the lockup and the menu. It sat on a second
          strip below while the menu button held this corner; with the menu
          moved to the far side the corner is free, and the game's name belongs
          in the app row rather than on a strip of its own.
        */}
        <div className={`game-badge tone-${art.tone}`}>
          <span className="game-badge-mark" aria-hidden="true">
            <Icon name={art.icon} />
          </span>
          <span className="game-badge-text">
            <span className="game-badge-name">{gameName}</span>
            {target !== null ? (
              <span className="game-badge-target">First to {target}</span>
            ) : null}
          </span>
        </div>

        {/*
          Dots alone, in the far corner.

          This carried the word "Table" beside them, on the argument that a
          bare glyph is a thing to decode. In practice the word made it a wide
          quiet slab that read as a label rather than a control - and three
          dots in the corner of a screen is one of the few glyphs that needs no
          decoding, which is why it is the one convention kept.

          The name survives for anything that speaks: `aria-label` is what a
          screen reader announces, so nothing was lost by taking the word off
          the screen.

          Last in the row, not first. The three cells are read in the order
          they are written, so moving it in the DOM is what actually moves it -
          a CSS `order` would put the button somewhere the keyboard and a
          screen reader still found it on the left.
        */}
        <button type="button" className="table-button" onClick={onMenu} aria-label="Table menu">
          <Icon name="menu-dots" />
        </button>
      </div>

      {/*
        Whatever is left over: the states no avatar can carry, and the score.

        Collapses to nothing when it holds neither - see `.table-strip` in the
        stylesheet. Crazy Eights publishes no target and no hand number, so for
        that game this row is genuinely empty and should not cost a gap.
      */}
      <div className="table-strip">
        <span className="table-bar-status">{status}</span>

        {hasPanel ? (
          <div className="score-panel">
            {hand !== null ? (
              <p className="score-line">
                <span className="score-key">Hand</span>
                <span className="score-value">{hand}</span>
              </p>
            ) : null}

            {/*
              Whose score depends on the game, and the layout is what says so.
              A partnership game's seats do not have scores of their own - in
              Spades a chair's own total is not a thing that exists.
            */}
            {sides ? (
              <p className="score-line">
                <span className="score-key">{layout.teams?.label ?? 'Team'}</span>
                <span className="score-value">
                  {sides.map((side) => side.score ?? '—').join(' – ')}
                </span>
              </p>
            ) : myScore !== null ? (
              <p className="score-line">
                <span className="score-key">{layout.seatScore?.label ?? 'Score'}</span>
                <span className="score-value">{myScore}</span>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
}
